/**
 * Classic project workspace shell:
 * - Workflow command bar with 3 activities (Discover → Understand → Operate)
 * - Nested activity tabs rendered by ProjectPhaseSubNav
 * - Outlet for activity pages
 * - Full-page ingestion gate while repositories are still being ingested
 */

import React, { Suspense, useEffect, useState, useSyncExternalStore } from 'react';
import { Box } from '@mui/material';
import { useAppStore } from '../../../store/appStore';
import RouteLoadingFallback from '../../common/RouteLoadingFallback';
import { ErrorState } from '../../../design-system/v2';
import { LAYOUT } from '../../../design-system/foundation';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useProject, useProjectWorkflow } from '../../../services/api';
import { isIngestionCompleted, shouldShowIngestionGate } from '../../../lib/ingestionGate';
import { getArchiveUpload, subscribeArchiveUploads, workspaceIntakeView } from '../../../lib/archiveUploadProgress';
import { clearBootstrapGateLock, lockBootstrapGate } from '../../../lib/bootstrapGateLock';
import ProjectWorkspaceHeader from './ProjectWorkspaceHeader';
import ProjectRecoveryBanner from './ProjectRecoveryBanner';
import IngestionProgressScreen from './IngestionProgressScreen';
import ArchiveUploadScreen from './ArchiveUploadScreen';
import { useProjectLifecycle } from './useProjectLifecycle';
import { ExperienceShell, PageTransition } from '../../../experience/v3';
import { WORKSPACE_CHROME_PAD } from '../../../experience/v3/workspaceSurface';
import { useRuntimeEventStore } from '../../../store/runtimeEventStore';
import { useQueryClient } from '@tanstack/react-query';
import { EngineeringWorkspaceChrome } from '../../../os';

/**
 * Key for the route-content fade: the project section (home, phase-0,
 * phase-1, operate, …). Tab switches inside a section keep the key, so they do
 * not remount the section layout; each section layout owns its own Suspense
 * boundary for tab content.
 */
function contentTransitionKey(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  const projectIdx = parts.indexOf('projects');
  return parts[projectIdx + 2] ?? 'home';
}

const ProjectWorkspaceLayout: React.FC = () => {
  const { projectId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isProjectHome = /\/projects\/[^/]+\/?$/.test(location.pathname);
  const lifecycle = useProjectLifecycle(projectId);
  const { project, isNotFound, isError } = useProject(projectId);
  const { data: workflow } = useProjectWorkflow(projectId);
  const compactDensity = useAppStore((s) => s.compactDensity);
  const [holdIngestionGate, setHoldIngestionGate] = useState(false);

  const qc = useQueryClient();
  const connectWs = useRuntimeEventStore((s) => s.connect);
  const disconnectWs = useRuntimeEventStore((s) => s.disconnect);

  const archiveUpload = useSyncExternalStore(
    subscribeArchiveUploads,
    () => getArchiveUpload(projectId),
    () => undefined,
  );
  const ingestionGateActive = shouldShowIngestionGate(project, workflow, undefined, projectId);

  useEffect(() => {
    if (!projectId || !project) return;
    if (isIngestionCompleted(project, workflow)) {
      clearBootstrapGateLock(projectId);
      return;
    }
    if (ingestionGateActive) {
      setHoldIngestionGate(true);
      lockBootstrapGate(projectId);
    }
  }, [ingestionGateActive, project, projectId, workflow]);

  // Hold the gate through the 100% completion flash before showing project home.
  const isIngesting = ingestionGateActive || holdIngestionGate;
  const intakeView = workspaceIntakeView(archiveUpload, isIngesting);
  const isRepositoryPage = /\/phase-0\/repository\/?$/.test(location.pathname);
  const isArtifactsPage = /\/phase-0\/artifacts\/?$/.test(location.pathname);
  const isCognitionSpace = /\/phase-1\/insights\/?$/.test(location.pathname);
  const isOperatePage = /\/operate\//.test(location.pathname);
  const flatShell =
    isProjectHome ||
    isRepositoryPage ||
    isArtifactsPage ||
    isCognitionSpace ||
    isOperatePage ||
    /\/phase-0\//.test(location.pathname) ||
    /\/phase-1\//.test(location.pathname);

  useEffect(() => {
    if (projectId) {
      connectWs(projectId, qc);
    }
    return () => {
      disconnectWs();
    };
  }, [projectId, connectWs, disconnectWs, qc]);

  useEffect(() => {
    if (isNotFound && projectId) {
      navigate('/projects/all', { replace: true });
    }
  }, [isNotFound, projectId, navigate]);

  // While ingesting, keep the user on the gate (project home path) rather than half-empty Discover.
  useEffect(() => {
    if (!projectId || !isIngesting) return;
    if (isProjectHome) return;
    navigate(`/projects/${projectId}`, { replace: true });
  }, [projectId, isIngesting, isProjectHome, navigate]);

  const showWorkspaceChrome = !isProjectHome && !isIngesting;
  // A failed load that isn't a 404 (404s redirect to the project list above).
  const loadFailed = isError && !isNotFound && !project;

  return (
    <Box className="v3-project-workspace" sx={{ width: '100%' }}>
      <Box
        className={showWorkspaceChrome ? 'workspace-chrome-shell' : undefined}
        // One horizontal padding for project home and every section page —
        // the app shell drops its own padding inside the project workspace.
        sx={{
          width: '100%',
          boxSizing: 'border-box',
          px: WORKSPACE_CHROME_PAD,
          pt: showWorkspaceChrome ? 1 : LAYOUT.pagePadTop,
        }}
      >
        {showWorkspaceChrome && (
          <ProjectWorkspaceHeader
            projectId={projectId}
            projectName={project?.name}
            sourceLanguage={project?.sourceLanguage}
            targetLanguage={project?.targetLanguage}
            status={project?.status}
            isIngesting={isIngesting}
            lifecycle={lifecycle}
          />
        )}

        <ProjectRecoveryBanner
          projectId={projectId}
          projectStatus={project?.status}
          ingestionStatus={project?.ingestionStatus}
          ingestionError={project?.ingestionError}
          projectUpdatedAt={project?.updatedAt}
        />

        {archiveUpload && (intakeView === 'uploading' || intakeView === 'upload-failed') ? (
          <ArchiveUploadScreen projectName={project?.name} upload={archiveUpload} />
        ) : loadFailed ? (
          <Box sx={{ maxWidth: LAYOUT.narrowMaxWidth, mx: 'auto', py: 4 }}>
            <ErrorState
              title="Couldn’t load this project"
              message="The project details didn’t load. Check your connection and try again."
              onRetry={() => qc.refetchQueries({ queryKey: ['projects', projectId] })}
            />
          </Box>
        ) : intakeView === 'ingestion' ? (
          <IngestionProgressScreen
            projectId={projectId}
            projectName={project?.name}
            onComplete={() => setHoldIngestionGate(false)}
          />
        ) : (
          <ExperienceShell pathname={location.pathname} flat={flatShell}>
            <Box
              className={flatShell ? 'workspace-ambient workspace-ambient-flat' : 'workspace-ambient animate-fade-in'}
              data-density={compactDensity ? 'compact' : 'comfortable'}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: isProjectHome ? 'calc(100vh - 48px)' : 'calc(100vh - 120px)',
                flex: isProjectHome ? 1 : undefined,
              }}
            >
              <EngineeringWorkspaceChrome>
                <PageTransition routeKey={contentTransitionKey(location.pathname)}>
                  {/* Inside the keyed transition, so entering a section mounts a
                      fresh boundary and shows its skeleton under the header. */}
                  <Suspense key={location.pathname} fallback={<RouteLoadingFallback />}>
                    <Outlet context={{ lifecycle, project }} />
                  </Suspense>
                </PageTransition>
              </EngineeringWorkspaceChrome>
            </Box>
          </ExperienceShell>
        )}
      </Box>
    </Box>
  );
};

export default ProjectWorkspaceLayout;
